import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useAvailability, useGuestSearch, useInvalidateStay } from '../api/queries';
import type { Booking } from '../api/types';
import { addDays, formatDate, formatGhs, nightsBetween } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

export function NewBooking() {
  const { me, can } = useAuth();
  const today = me?.property.today ?? '';
  const nav = useNavigate();
  const toast = useToast();
  const report = useReport();
  const inv = useInvalidateStay();

  const [checkIn, setCheckIn] = useState(today);
  const [checkOut, setCheckOut] = useState(addDays(today, 1));
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [choice, setChoice] = useState<{ roomTypeId: string; ratePlanId: string } | null>(null);

  const [guestMode, setGuestMode] = useState<'existing' | 'new'>('new');
  const [search, setSearch] = useState('');
  const [guestId, setGuestId] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [confirmNow, setConfirmNow] = useState(true);
  const [discountPct, setDiscountPct] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const nights = checkIn && checkOut ? nightsBetween(checkIn, checkOut) : 0;
  const datesOk = nights >= 1 && nights <= 60 && checkIn >= today;
  const avail = useAvailability(checkIn, checkOut, adults, datesOk);
  const found = useGuestSearch(guestMode === 'existing' ? search : '');

  // a new search invalidates any earlier choice
  useEffect(() => setChoice(null), [checkIn, checkOut, adults]);

  const picked = avail.data?.roomTypes.find((r) => r.id === choice?.roomTypeId);
  const quote = picked?.ratePlans.find((p) => p.id === choice?.ratePlanId)?.quote;

  const pct = discountPct.trim() === '' ? null : Number(discountPct);
  const pctOk = pct === null || (Number.isFinite(pct) && pct > 0 && pct <= 100);
  const guestOk = guestMode === 'existing' ? !!guestId : fullName.trim().length >= 2 && (phone.trim() === '' || /^\+?[0-9 ()-]{7,20}$/.test(phone.trim()));
  const canSubmit = !!choice && guestOk && pctOk && datesOk;

  const create = useMutation({
    mutationFn: () =>
      api<Booking>('/bookings', {
        method: 'POST',
        body: {
          roomTypeId: choice!.roomTypeId,
          ratePlanId: choice!.ratePlanId,
          checkIn,
          checkOut,
          adults,
          children,
          confirm: confirmNow,
          source: 'FRONT_DESK',
          ...(guestMode === 'existing' ? { guestId } : { guest: { fullName: fullName.trim(), ...(phone.trim() ? { phone: phone.trim() } : {}) } }),
          ...(pct !== null && can('rates.manage') ? { discount: { type: 'PERCENT', bps: Math.round(pct * 100) } } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      }),
    onSuccess: (b) => {
      toast.ok(`Booking ${b.reference} created`);
      inv();
      nav(`/bookings/${b.id}`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'NO_AVAILABILITY') {
        setFormError('Those rooms were just taken. Choose different dates or another room type.');
        void avail.refetch();
      } else report(e);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (canSubmit) create.mutate();
  }

  return (
    <form className="page" onSubmit={submit}>
      <PageHead eyebrow="Front desk" title="New booking" />

      <section className="card stack" aria-labelledby="s1">
        <h2 className="h2" id="s1">
          1 · Dates and guests
        </h2>
        <div className="grid-2">
          <Field id="ci" label="Check-in">
            <input
              id="ci"
              className="input"
              type="date"
              min={today}
              value={checkIn}
              onChange={(e) => {
                setCheckIn(e.target.value);
                if (e.target.value && checkOut <= e.target.value) setCheckOut(addDays(e.target.value, 1));
              }}
            />
          </Field>
          <Field id="co" label="Check-out" hint={nights >= 1 ? `${nights} night${nights === 1 ? '' : 's'}` : undefined} error={nights < 1 ? 'Check-out must be after check-in' : nights > 60 ? 'For stays over 60 nights, book in parts' : undefined}>
            <input id="co" className="input" type="date" min={addDays(checkIn || today, 1)} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
          </Field>
          <Field id="ad" label="Adults">
            <input id="ad" className="input" type="number" min={1} max={10} value={adults} onChange={(e) => setAdults(Math.max(1, Number(e.target.value) || 1))} />
          </Field>
          <Field id="ch" label="Children">
            <input id="ch" className="input" type="number" min={0} max={10} value={children} onChange={(e) => setChildren(Math.max(0, Number(e.target.value) || 0))} />
          </Field>
        </div>
      </section>

      <section className="stack" aria-labelledby="s2">
        <h2 className="h2" id="s2">
          2 · Choose a room
        </h2>
        {!datesOk ? (
          <div className="card flat">
            <Empty icon="rooms" title="Pick valid dates to see what is free" />
          </div>
        ) : avail.isLoading ? (
          <Loading rows={2} />
        ) : avail.error ? (
          <ErrorNote error={avail.error} retry={() => void avail.refetch()} />
        ) : (avail.data?.roomTypes ?? []).length === 0 ? (
          <div className="card flat">
            <Empty icon="rooms" title="No room types set up yet">
              An owner or manager needs to add room types and rates first.
            </Empty>
          </div>
        ) : (
          <div className="list" role="radiogroup" aria-label="Room options">
            {avail.data!.roomTypes.map((rt) => (
              <div key={rt.id} className="stack">
                {rt.ratePlans.length === 0 ? (
                  <div className="choice">
                    <strong>{rt.name}</strong>
                    <span className="small muted">No active rate plan.</span>
                  </div>
                ) : (
                  rt.ratePlans.map((p) => {
                    const checked = choice?.ratePlanId === p.id;
                    const disabled = !rt.bookable;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        disabled={disabled}
                        className="choice"
                        style={{ textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
                        onClick={() => setChoice({ roomTypeId: rt.id, ratePlanId: p.id })}
                      >
                        <div className="row" style={{ justifyContent: 'space-between' }}>
                          <strong>{rt.name}</strong>
                          {rt.bookable ? <Badge tone="good">{rt.available} free</Badge> : <Badge tone="bad">Full</Badge>}
                        </div>
                        <div className="row small muted">{p.name}</div>
                        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <span className="price num">{formatGhs(p.quote.total)}</span>
                          <span className="small muted">
                            {nights} night{nights === 1 ? '' : 's'}
                            {p.quote.taxes.length ? ' · tax included' : ''}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card stack" aria-labelledby="s3">
        <h2 className="h2" id="s3">
          3 · Guest
        </h2>
        <div className="seg" role="group" aria-label="Guest">
          <button type="button" aria-pressed={guestMode === 'new'} onClick={() => setGuestMode('new')}>
            New guest
          </button>
          <button type="button" aria-pressed={guestMode === 'existing'} onClick={() => setGuestMode('existing')}>
            Returning guest
          </button>
        </div>

        {guestMode === 'new' ? (
          <div className="grid-2">
            <Field id="gn" label="Full name">
              <input id="gn" className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="off" />
            </Field>
            <Field id="gp" label="Phone" hint="Optional, but useful for confirmations">
              <input id="gp" className="input" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" />
            </Field>
          </div>
        ) : (
          <div className="stack">
            <Field id="gs" label="Search by name or phone">
              <input id="gs" className="input" value={search} onChange={(e) => { setSearch(e.target.value); setGuestId(null); }} autoComplete="off" />
            </Field>
            {found.isFetching ? <Loading rows={1} /> : null}
            <div className="list">
              {(found.data ?? []).map((g) => (
                <button key={g.id} type="button" className="item" aria-pressed={guestId === g.id} style={{ textAlign: 'left', cursor: 'pointer', borderColor: guestId === g.id ? 'var(--brand)' : undefined }} onClick={() => setGuestId(g.id)}>
                  <span className="title">{g.fullName}</span>
                  <span className="small muted">{g.phone ?? 'No phone'}</span>
                </button>
              ))}
              {search.trim().length >= 2 && !found.isFetching && (found.data ?? []).length === 0 ? <p className="small muted">No guest found. Switch to “New guest” to add them.</p> : null}
            </div>
          </div>
        )}

        <Field id="nt" label="Notes (optional)">
          <textarea id="nt" className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
        </Field>

        {can('rates.manage') ? (
          <Field id="dc" label="Discount % (managers only)" error={!pctOk ? 'Enter a number between 1 and 100' : undefined}>
            <input id="dc" className="input" inputMode="decimal" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} placeholder="0" />
          </Field>
        ) : null}

        <label className="row" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={confirmNow} onChange={(e) => setConfirmNow(e.target.checked)} style={{ width: 22, height: 22 }} />
          <span>
            Confirm now
            <span className="hint" style={{ display: 'block' }}>
              Untick to hold the room for an hour instead.
            </span>
          </span>
        </label>
      </section>

      {formError ? (
        <div className="banner bad" role="alert">
          {formError}
        </div>
      ) : null}

      <div className="card flat row" style={{ justifyContent: 'space-between' }}>
        <div>
          <div className="eyebrow">Total</div>
          <div className="price num">{quote ? formatGhs(quote.total) : '–'}</div>
          {quote ? (
            <div className="hint">
              {formatDate(checkIn)} → {formatDate(checkOut)}
              {pct ? ` · ${pct}% discount applied on booking` : ''}
            </div>
          ) : null}
        </div>
        <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
          {confirmNow ? 'Create booking' : 'Hold room'}
        </Button>
      </div>
    </form>
  );
}
